import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'
import CareModelDiagram from '@/components/landing/CareModelDiagram'
import WearableLogo from '@/components/WearableLogo'
import Seo from '@/components/Seo'
import { useScrollReveal } from '@/hooks/useScrollReveal'
import { signUpUrl } from '@/lib/auth'
import './landing.css'
import './product/product.css'

function ArrowIcon() {
  return (
    <svg className="btn-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  )
}

// What PelviaCare+ includes — the concrete deliverables that turn the care model
// into something a patient can trust and commit to.
const INCLUDED = [
  {
    title: 'A physician-grade intake',
    body: 'An intake built for the language of pelvic pain: hormonal cycles, flare triggers, symptom patterns. Done on your own time, by voice or text, no rushed 15-minute visit.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M9 13h6M9 17h4" />
      </svg>
    ),
  },
  {
    title: 'A board-certified sign-off',
    body: 'A licensed OB/GYN reviews your presentation and signs a structured clinical note. That note is your official diagnosis, not a guess from the internet.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    ),
  },
  {
    title: 'Excision-surgeon referrals',
    body: 'The patient-preferred excision surgeons, organized by state and listed clearly. You choose who you see, and we send the referral. If surgery isn’t where you are yet, there’s a real path for that too.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: 'A clinician-ready record',
    body: 'You arrive with a complete clinical note, daily pain tracking, validated symptom scores, and imaging orders already placed. The surgeon is ready for you on day one.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 11H3v10h6zM21 3h-6v18h6zM15 7H9v14h6z" />
      </svg>
    ),
  },
  {
    title: 'A pain diary that works for you',
    body: 'A daily pain diary runs alongside every step, turning a few taps into the patterns that take clinicians years to spot, and an AI health coach that answers in the context of your own data.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m12 3 1.9 5.8H20l-4.9 3.6 1.9 5.8L12 14.6l-5 3.6 1.9-5.8L4 8.8h6.1z" />
      </svg>
    ),
  },
  {
    title: 'Private and encrypted',
    body: 'Your data is encrypted, de-identified for research, and only ever visible to you. We never sell your personal data. No data brokers, no exceptions.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
]

export default function PelviaCarePage() {
  useScrollReveal()

  return (
    <div className="landing product-page pelviacare-page">
      <Seo
        title="PelviaCare+ | From symptom to surgeon referral in 72 hours"
        description="PelviaCare+ is Pelvia’s full care model: a guided clinical path from your first symptom to a board-certified sign-off and an excision-surgeon referral, in 72 hours instead of seven years."
        path="/pelviacare"
        image="/static/og/pelviacare.png"
      />
      <SiteHeader />

      <section className="product-hero">
        <div className="section-label">PelviaCare+</div>
        <h1>
          From your first symptom to a surgeon <em>who’s ready for you.</em>
        </h1>
        <p className="product-hero-sub">
          PelviaCare+ is the full Pelvia care model, a guided clinical path that collapses the
          seven-year wait into 72 hours. Not another app to manage your pain, but a sequence that
          carries you from symptom to diagnosis to the right specialist, with a physician’s name on
          every step.
        </p>
        <div className="hero-actions">
          <a href="/#app-waitlist" className="btn-primary">
            Join the PelviaCare+ waitlist
            <ArrowIcon />
          </a>
          <a href={signUpUrl()} className="btn-secondary">
            Start your free intake
          </a>
        </div>
      </section>

      <section className="pelviacare-idea">
        <div className="section-container">
          <p className="pelviacare-idea-lede reveal">
            <span className="pelviacare-idea-big">Seven years.</span> That’s how long the average
            woman waits for a diagnosis, told her pain is normal while a disease that affects 1 in
            10 women goes unnamed.
          </p>
          <p className="reveal reveal-delay-1">
            That wait isn’t a gap in medicine. It’s a failure of access: no one connecting the
            dots, ordering the right note, and pointing you to the right surgeon.{' '}
            <strong>PelviaCare+ is the path that does all three</strong>, in a fixed sequence, so
            nothing falls through the cracks and no one’s pain gets dismissed again.
          </p>
        </div>
      </section>

      <section className="pelviacare-sequence">
        <div className="section-container">
          <div className="product-features-header">
            <div className="section-label reveal">The sequence</div>
            <h2 className="section-title reveal reveal-delay-1">
              Four steps. <em>Seventy-two hours.</em>
            </h2>
            <p className="section-desc reveal reveal-delay-2">
              Every PelviaCare+ patient moves through the same clinical path, each step handing the
              next everything it needs, so you’re never starting over or explaining yourself twice.
            </p>
          </div>
          <CareModelDiagram />
        </div>
      </section>

      <section className="product-features">
        <div className="section-container">
          <div className="product-features-header">
            <div className="section-label reveal">What’s included</div>
            <h2 className="section-title reveal reveal-delay-1">
              Everything the seven-year wait <em>never gave you.</em>
            </h2>
            <p className="section-desc reveal reveal-delay-2">
              PelviaCare+ isn’t a subscription to symptom-tracking. It’s a complete clinical package,
              built so you walk into specialist care already believed.
            </p>
          </div>
          <div className="product-feature-grid pelviacare-included-grid">
            {INCLUDED.map((f, i) => (
              <div key={f.title} className={`product-feature reveal${i % 3 === 1 ? ' reveal-delay-1' : i % 3 === 2 ? ' reveal-delay-2' : ''}`}>
                <div className="product-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pelviacare-trust">
        <div className="pelviacare-trust-inner">
          <div className="section-label reveal">Why you can commit to this</div>
          <h2 className="section-title reveal reveal-delay-1">
            Designed by a physician. <em>Built to be believed.</em>
          </h2>
          <p className="section-desc reveal reveal-delay-2">
            PelviaCare+ was designed by a board-certified OB/GYN and an REI fellow at the Cleveland
            Clinic, and every diagnosis carries a licensed physician’s sign-off. The same rigor
            that takes years in the traditional system, delivered in days.
          </p>
          <div className="pelviacare-trust-logos reveal reveal-delay-3">
            <span className="pelviacare-trust-label">Backed by</span>
            <img
              className="backed-by-logo"
              src="/static/app/uconn-health-logo.svg"
              alt="UConn Health"
              width={236}
              height={28}
            />
            <span className="product-wearables-divider" aria-hidden="true" />
            <span className="pelviacare-trust-label">Works with</span>
            <WearableLogo provider="oura" fallbackName="Oura" height={22} />
            <WearableLogo provider="whoop" fallbackName="Whoop" height={26} />
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="final-cta-inner">
          <div className="section-label reveal">Limited early access</div>
          <h2 className="reveal reveal-delay-1">
            Be first in line for <em>PelviaCare+.</em>
          </h2>
          <p className="reveal reveal-delay-2">
            We’re opening PelviaCare+ to a small group first. Join the waitlist to claim early
            access, or start your free intake today and begin building your evidence now.
          </p>
          <div className="hero-actions reveal reveal-delay-3">
            <a href="/#app-waitlist" className="btn-primary">
              Join the PelviaCare+ waitlist
              <ArrowIcon />
            </a>
            <a href={signUpUrl()} className="btn-secondary">
              Start your free intake
            </a>
          </div>
          <p className="final-cta-privacy reveal reveal-delay-4">
            Free to join. No spam. Your data is encrypted and only ever visible to you.
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
